/**
 * Align browser/OS back (POP) with hierarchical named parents + in-flow step/overlay dismiss.
 *
 * DEFER: true `useBlocker` requires `createBrowserRouter` / data-router migration.
 * This app still uses `BrowserRouter`, so we detect POP via `useNavigationType` and
 * rewrite (or undo) with `navigate(..., { replace: true })`. Residual: no synchronous
 * block before the history entry commits — undo/rewrite runs in the post-POP layout effect.
 *
 * useLayoutEffect (not useEffect): checkout's usePublishHierarchicalBackFlow clears
 * onStepBack in a useEffect cleanup. Layout must consumeStepBack before that clear,
 * otherwise page.goBack() from QR leaves the user on shop (MOB-05).
 */

import { useLayoutEffect, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { useOptionalJourneyContext } from '../shared/context/JourneyContext.js';
import { useOptionalHierarchicalBackFlow } from '../shared/context/HierarchicalBackFlowContext.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import {
  inferJourneyGateFromPath,
  resolveSystemBackDecision,
} from '../navigation/hierarchicalBack.js';
import {
  isEphemeralBackRestorePath,
  resolveEphemeralBackFallback,
} from '../navigation/ephemeralBackRestore.js';
import type { MasterScreenId } from '../registry/masterScreenRegistry.types.js';
import { isAccountOrderDetailToOrdersListPop } from '../features/account/AccountShell.js';

function stripHash(href: string): string {
  const i = href.indexOf('#');
  return i >= 0 ? href.slice(0, i) : href;
}

function pathnameOf(href: string): string {
  const noHash = stripHash(href);
  return noHash.split('?')[0] ?? noHash;
}

function hashOf(href: string): string {
  const i = href.indexOf('#');
  return i >= 0 ? href.slice(i) : '';
}

/** W5 registry: pickup/verify = MS-PICK-040 (not Find Order MS-PICK-041). */
function isPickupVerifyPath(pathname: string): boolean {
  return /\/pickup\/verify(?:\/|$)/.test(pathname);
}

/** Mount once under CustomerAppShell (tenant + platform chrome). */
export function useHierarchicalSystemBack(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const tenantCode = useTenantCode();
  const journeyCtx = useOptionalJourneyContext();
  const flowCtx = useOptionalHierarchicalBackFlow();
  const overlayCtx = useOptionalOverlayBackDismiss();

  const previousPathRef = useRef(location.pathname);
  const previousSearchRef = useRef(location.search);
  const previousHashRef = useRef(location.hash);
  const rewritingRef = useRef(false);
  /** Once-per-POP: Strict Mode re-runs effects; never double-consumeStepBack. */
  const popHandledRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const leftPath = previousPathRef.current;
    const leftSearch = previousSearchRef.current;
    const leftHash = previousHashRef.current;
    const nextPath = location.pathname;

    if (rewritingRef.current) {
      rewritingRef.current = false;
      previousPathRef.current = nextPath;
      previousSearchRef.current = location.search;
      previousHashRef.current = location.hash;
      return;
    }

    // Same-URL in-flow POP (checkout/donation pushed history for each step — MOB-05).
    // History already sits on the correct URL; only consumeStepBack / overlay dismiss.
    const sameUrlPop =
      navigationType === 'POP' &&
      leftPath === nextPath &&
      leftSearch === location.search;

    if (sameUrlPop) {
      const popToken = `${location.key}:same-url:${leftPath}${leftSearch}`;
      if (popHandledRef.current === popToken) {
        previousPathRef.current = nextPath;
        previousSearchRef.current = location.search;
        previousHashRef.current = location.hash;
        return;
      }
      popHandledRef.current = popToken;

      const overlayDismiss = overlayCtx?.overlayDismiss ?? null;
      if (overlayDismiss !== null) {
        overlayDismiss();
        previousPathRef.current = nextPath;
        previousSearchRef.current = location.search;
        previousHashRef.current = location.hash;
        return;
      }

      if (flowCtx?.consumeStepBack() === true) {
        previousPathRef.current = nextPath;
        previousSearchRef.current = location.search;
        previousHashRef.current = location.hash;
        return;
      }

      previousPathRef.current = nextPath;
      previousSearchRef.current = location.search;
      previousHashRef.current = location.hash;
      return;
    }

    if (navigationType === 'POP' && leftPath !== nextPath) {
      // Key by history entry + left/next so the same POP cannot run twice.
      const popToken = `${location.key}:${leftPath}->${nextPath}`;
      if (popHandledRef.current === popToken) {
        previousPathRef.current = nextPath;
        previousSearchRef.current = location.search;
        previousHashRef.current = location.hash;
        return;
      }
      popHandledRef.current = popToken;

      const restoreLeft = (): void => {
        rewritingRef.current = true;
        navigate(`${leftPath}${leftSearch}${leftHash}`, { replace: true });
        previousPathRef.current = leftPath;
        previousSearchRef.current = leftSearch;
        previousHashRef.current = leftHash;
      };

      // 1) Overlay dismiss-first (sheet closes; stay on left URL — undo POP).
      // Exception (G5): account order detail → orders list hub — accept POP to list
      // (close already replace-navigates to hub; restoreLeft would reopen /:id).
      // Order: overlay → consumeStepBack → stepBackTo / hierarchical. Never navigate(-1).
      const overlayDismiss = overlayCtx?.overlayDismiss ?? null;
      if (overlayDismiss !== null) {
        overlayDismiss();
        if (isAccountOrderDetailToOrdersListPop(leftPath, nextPath)) {
          previousPathRef.current = nextPath;
          previousSearchRef.current = location.search;
          previousHashRef.current = location.hash;
          return;
        }
        restoreLeft();
        return;
      }

      // 2) Same-URL in-flow step (checkout/donation) — undo POP, no route navigate.
      // When flow.backDisabled, consumeStepBack returns true without calling onStepBack
      // so restoreLeft keeps the URL and the checkout/donation step does not advance.
      if (flowCtx?.consumeStepBack() === true) {
        restoreLeft();
        return;
      }

      // 3) Named parent / journey gate — MS from left-path inference (DOM dropped).
      // In-flow SoT: HierarchicalBackFlow.stepBackTo (+ consumeStepBack above).
      const inferred = inferJourneyGateFromPath(leftPath, tenantCode);
      const stepBackTo = flowCtx?.stepBackTo ?? null;

      let masterScreenId: MasterScreenId | null = inferred?.masterScreenId ?? null;
      let journeyId = journeyCtx?.journeyId ?? inferred?.journeyId ?? null;
      // W5: pickup/verify is MS-PICK-040 — override stale infer (041 = Find Order).
      if (isPickupVerifyPath(leftPath)) {
        masterScreenId = 'MS-PICK-040';
        journeyId = journeyCtx?.journeyId ?? inferred?.journeyId ?? 'J-PICKUP-VERIFY';
      }

      const decision = resolveSystemBackDecision(leftPath, tenantCode, {
        journeyId,
        masterScreenId,
        stepBackTo,
        search: leftSearch,
      });

      if (decision.action === 'redirect') {
        const target = decision.to;
        const targetPathname = pathnameOf(target);
        const targetHash = hashOf(target);
        const landedPathname = nextPath;
        const hashMismatch =
          targetHash.length > 0 && location.hash !== targetHash;

        // Compare without hash; still rewrite when parent hash must be applied.
        if (targetPathname !== landedPathname || hashMismatch) {
          // Never redirect into a stale payment-monitoring / QR / complete URL.
          if (isEphemeralBackRestorePath(targetPathname)) {
            const safe = resolveEphemeralBackFallback(targetPathname, tenantCode);
            rewritingRef.current = true;
            navigate(safe, { replace: true });
            previousPathRef.current = pathnameOf(safe);
            previousSearchRef.current = '';
            previousHashRef.current = '';
            return;
          }
          rewritingRef.current = true;
          navigate(target, { replace: true });
          previousPathRef.current = targetPathname;
          previousSearchRef.current = '';
          previousHashRef.current = targetHash;
          return;
        }
      }

      // POP allowed by hierarchical policy but history landed on a done payment
      // surface (e.g. Organizace ← into checkout/complete) — bounce to safe parent.
      if (isEphemeralBackRestorePath(nextPath)) {
        const safe = resolveEphemeralBackFallback(nextPath, tenantCode);
        rewritingRef.current = true;
        navigate(safe, { replace: true });
        previousPathRef.current = pathnameOf(safe);
        previousSearchRef.current = '';
        previousHashRef.current = '';
        return;
      }
    }

    previousPathRef.current = nextPath;
    previousSearchRef.current = location.search;
    previousHashRef.current = location.hash;
  }, [
    flowCtx,
    journeyCtx?.journeyId,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    navigate,
    navigationType,
    overlayCtx?.overlayDismiss,
    tenantCode,
  ]);
}
